import React, { useState, useEffect } from 'react';
import styles from './widgetStyles.module.css';
import BuyMeACoffee from './buyMeACoffeeCustom.jsx';
import { isMobile, isIOS, isAndroid } from '../utils/deviceUtils';

const ShareImagesMenu = ({ isVisible, onDownload }) => {
    const [iscollapsed, setIscollapsed] = useState(true);
    const [selectedImage, setSelectedImage] = useState(null);
    const [previewImages, setPreviewImages] = useState({ normal: null, zoomed: null });

    useEffect(() => {
        if (!iscollapsed && onDownload) {
            generatePreviewImages();
        }
    }, [iscollapsed, onDownload]);

    const selectedColor = '#ffffff';
    const shareTitle = "Check out my music taste visualization!";
    const shareText = "I created this interactive graph of my music network. Create yours too: https://musicgrapher.com/";

    // Generate preview images when menu opens
    const generatePreviewImages = async () => {
        if (onDownload) {
            // Generate normal view (not zoomed out, shows musician names)
            const normalCanvas = await onDownload(false, true); // false = no overlay, true = return canvas
            if (normalCanvas) {
                setPreviewImages(prev => ({ ...prev, normal: normalCanvas.toDataURL() }));
            }

            // Generate zoomed out view (shows cluster tags)
            const zoomedCanvas = await onDownload(true, true); // true = with overlay/zoomed, true = return canvas
            if (zoomedCanvas) {
                setPreviewImages(prev => ({ ...prev, zoomed: zoomedCanvas.toDataURL() }));
            }
        }
    };

    // Handle image click to open share menu
    const handleImageClick = (imageType) => {
        setSelectedImage(imageType);
    };

    // Download image with custom filename
    const openImageInNewTab = (canvas) => {
        const dataUrl = canvas.toDataURL('image/png');
        try {
            const win = window.open();
            if (win) {
                win.document.write('<img src="' + dataUrl + '" style="width:100%;height:auto;" />');
            } else {
                // Fallback: download the image if popup is blocked
                const link = document.createElement('a');
                link.download = `music-graph-${Date.now()}.png`;
                link.href = dataUrl;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }
        } catch (error) {
            // Fallback: download the image if there's any error
            const link = document.createElement('a');
            link.download = `music-graph-${Date.now()}.png`;
            link.href = dataUrl;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    // Helper: Check if Web Share API supports files
    const canWebShareFiles = (file) => {
        return (
            navigator.share &&
            navigator.canShare &&
            navigator.canShare({ files: [file] })
        );
    };

    // Mobile Save Image: for iOS, always open image in new tab with instructions
    const handleMobileSave = async (imageType) => {
        try {
            const canvas = imageType === 'normal' ?
                await onDownload(false, true) :
                await onDownload(true, true);
            if (!canvas) {
                alert('Unable to generate image.');
                return;
            }
            const dataUrl = canvas.toDataURL('image/png');
            if (isIOS()) {
                // Always open in new tab with instructions for iOS
                const win = window.open('', '_blank');
                if (win) {
                    win.document.write(`
                        <html>
                            <head>
                                <meta name="viewport" content="width=device-width, initial-scale=1.0">
                                <title>Save to Photos</title>
                                <style>
                                    body { background: #000; color: #fff; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif; text-align: center; margin: 0; padding: 20px; }
                                    img { max-width: 100%; height: auto; border-radius: 8px; margin: 20px 0; border: 2px solid rgba(255,255,255,0.2); }
                                    .instructions { margin-bottom: 20px; font-size: 18px; padding: 20px; background: rgba(255,255,255,0.1); border-radius: 12px; line-height: 1.4; }
                                    .step { margin: 10px 0; padding: 10px; background: rgba(255,255,255,0.05); border-radius: 8px; font-size: 16px; }
                                </style>
                            </head>
                            <body>
                                <div class="instructions">
                                    📱 <strong>To Save to Photos:</strong><br>
                                    Long press the image below and select <b>"Add to Photos"</b> or <b>"Save Image"</b>.<br><br>
                                    <span style="font-size:15px;">If you don't see the option, try copying the image and pasting it in the Photos app.</span>
                                </div>
                                <img src="${dataUrl}" alt="Music Graph" />
                                <div class="step">
                                    <b>Tip:</b> You can also share this image using the iOS share sheet after long-pressing.
                                </div>
                            </body>
                        </html>
                    `);
                    win.document.close();
                } else {
                    alert('Unable to open new tab. Please try again or check your browser settings.');
                }
            } else if (isAndroid()) {
                // For Android, trigger download
                const link = document.createElement('a');
                link.download = `music-graph-${imageType}-${Date.now()}.png`;
                link.href = dataUrl;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            } else {
                // Fallback for other mobile devices
                openImageInNewTab(canvas);
            }
        } catch (error) {
            alert('Unable to save image. Please try using the Share button instead.');
        }
        setSelectedImage(null);
    };

    // Mobile Share Image: use Web Share API with file support if available, otherwise fallback to new tab with instructions
    const handleMobileShare = async (imageType) => {
        try {
            const canvas = imageType === 'normal' ?
                await onDownload(false, true) :
                await onDownload(true, true);
            if (!canvas) {
                alert('Unable to generate image.');
                return;
            }
            const filename = `music-graph-${imageType}-${Date.now()}.png`;
            canvas.toBlob(async (blob) => {
                const file = new File([blob], filename, { type: 'image/png' });
                if (canWebShareFiles(file)) {
                    try {
                        await navigator.share({
                            title: shareTitle,
                            text: shareText,
                            files: [file],
                        });
                        setSelectedImage(null);
                        return;
                    } catch (err) {
                        // If user cancels or error, fallback
                    }
                }
                // Fallback: open in new tab with share instructions for iOS
                const dataUrl = canvas.toDataURL('image/png');
                if (isIOS()) {
                    const win = window.open('', '_blank');
                    if (win) {
                        win.document.write(`
                            <html>
                                <head>
                                    <meta name="viewport" content="width=device-width, initial-scale=1.0">
                                    <title>Share Image</title>
                                    <style>
                                        body { background: #000; color: #fff; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif; text-align: center; margin: 0; padding: 20px; }
                                        img { max-width: 100%; height: auto; border-radius: 8px; margin: 20px 0; border: 2px solid rgba(255,255,255,0.2); }
                                        .instructions { margin-bottom: 20px; font-size: 18px; padding: 20px; background: rgba(255,255,255,0.1); border-radius: 12px; line-height: 1.4; }
                                        .step { margin: 10px 0; padding: 10px; background: rgba(255,255,255,0.05); border-radius: 8px; font-size: 16px; }
                                    </style>
                                </head>
                                <body>
                                    <div class="instructions">
                                        📱 <strong>To Share:</strong><br>
                                        Long press the image below and select <b>"Share"</b>.<br><br>
                                        <span style="font-size:15px;">Or copy the image and paste it in your chat or app.</span>
                                    </div>
                                    <img src="${dataUrl}" alt="Music Graph" />
                                    <div class="step">
                                        <b>Tip:</b> You can also save this image to Photos by long-pressing and choosing "Add to Photos".
                                    </div>
                                </body>
                            </html>
                        `);
                        win.document.close();
                    } else {
                        alert('Unable to open new tab. Please try again or check your browser settings.');
                    }
                } else {
                    // Android/other fallback
                    openImageInNewTab(canvas);
                }
                setSelectedImage(null);
            }, 'image/png');
        } catch (error) {
            alert('Unable to share image. Please try saving it first or copy this link: ' + window.location.href);
            setSelectedImage(null);
        }
    };

    // Desktop download image helper
    const downloadImage = (canvas, filename) => {
        const link = document.createElement('a');
        link.download = filename;
        link.href = canvas.toDataURL('image/png');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Handle desktop social media sharing
    const handleDesktopShare = async (platform, imageType) => {
        const canvas = imageType === 'normal' ?
            await onDownload(false, true) :
            await onDownload(true, true);

        if (!canvas) return;

        const filename = `music-graph-${imageType}-${Date.now()}.png`;
        const shareUrl = window.location.href;

        // Convert canvas to blob for sharing
        canvas.toBlob(async (blob) => {
            switch (platform) {
                case 'download':
                    downloadImage(canvas, filename);
                    break;
                case 'twitter':
                    // Download the image and open Twitter sharing
                    downloadImage(canvas, filename);
                    const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`;
                    window.open(twitterUrl, '_blank');
                    break;
                case 'facebook':
                    // Download the image and open Facebook sharing
                    downloadImage(canvas, filename);
                    const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
                    window.open(facebookUrl, '_blank');
                    break;
                case 'reddit':
                    // Download the image and open Reddit sharing
                    downloadImage(canvas, filename);
                    const redditUrl = `https://reddit.com/submit?url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(shareTitle)}`;
                    window.open(redditUrl, '_blank');
                    break;
                case 'whatsapp':
                    // Download the image and open WhatsApp sharing
                    downloadImage(canvas, filename);
                    const whatsappText = `${shareText} ${shareUrl}`;
                    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(whatsappText)}`;
                    window.open(whatsappUrl, '_blank');
                    break;
                case 'telegram':
                    // Download the image and open Telegram sharing
                    downloadImage(canvas, filename);
                    const telegramUrl = `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`;
                    window.open(telegramUrl, '_blank');
                    break;
                default:
                    break;
            }
        }, 'image/png');

        setSelectedImage(null);
    };

    // Styles matching help menu theme
    const menuStyle = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: window.innerWidth < 768 ? '90%' : 'auto',
        height: window.innerWidth < 768 ? '80vh' : 'auto',
        color: selectedColor,
        fontFamily: 'Arial',
        backgroundColor: 'rgba(0, 0, 0, 0.2)',
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
        overflow: 'auto',
        zIndex: '100',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        borderRadius: '8px',
        padding: window.innerWidth < 768 ? '20px' : '40px',
        opacity: !iscollapsed ? 1 : 0,
        visibility: !iscollapsed ? 'visible' : 'hidden',
        transition: 'all 0.5s ease-in-out',
    };

    const closeButtonStyle = {
        position: 'absolute',
        top: '20px',
        right: '20px',
        background: 'none',
        border: 'none',
        color: selectedColor,
        fontSize: '24px',
        cursor: 'pointer',
        padding: '8px',
    };

    const imagesContainerStyle = {
        display: 'flex',
        gap: '20px',
        marginBottom: '30px',
        flexDirection: window.innerWidth < 768 ? 'column' : 'row',
    };

    const imageCardStyle = {
        flex: 1,
        border: '1px solid rgba(255, 255, 255, 0.3)',
        borderRadius: '8px',
        overflow: 'hidden',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
    };

    const imageStyle = {
        width: '100%',
        height: '150px',
        objectFit: 'cover',
        display: 'block',
    };

    const labelStyle = {
        padding: '12px',
        textAlign: 'center',
        fontSize: '14px',
        fontWeight: 'bold',
        background: 'rgba(255, 255, 255, 0.1)',
    };

    const shareMenuStyle = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: window.innerWidth < 768 ? '90%' : 'auto',
        color: selectedColor,
        fontFamily: 'Arial',
        backgroundColor: 'rgba(0, 0, 0, 0.2)',
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
        overflow: 'auto',
        zIndex: '200',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        borderRadius: '8px',
        padding: '30px',
    };

    const shareButtonsContainerStyle = {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '12px',
        justifyContent: 'center',
        alignItems: 'center',
        marginTop: '20px',
    };

    const shareButtonStyle = {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '12px 16px',
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        borderRadius: '8px',
        color: selectedColor,
        cursor: 'pointer',
        fontSize: '14px',
        transition: 'all 0.2s ease',
        textDecoration: 'none',
        minWidth: '120px',
        justifyContent: 'center',
    };

    const mobileShareButtonStyle = {
        ...shareButtonStyle,
        minWidth: '140px',
        fontSize: '16px',
        padding: '16px 20px',
    };

    return (
        <>
            {/* Bootstrap Icons CDN */}
            <link
                href="https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.0/font/bootstrap-icons.css"
                rel="stylesheet"
            />

            {/* Share Icon Button */}
            <button
                className={styles.buttonSquare}
                onClick={() => setIscollapsed(false)}
                style={{
                    position: 'absolute',
                    top: '80px',
                    right: '20px',
                    zIndex: 3,
                    padding: '8px 16px',
                    width: '3em',
                    height: '3em',
                    cursor: 'pointer',
                    opacity: isVisible && iscollapsed ? 1 : 0,
                    visibility: isVisible && iscollapsed ? 'visible' : 'hidden',
                    transition: 'all 0.5s ease-in-out',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <i className="bi bi-share" style={{ fontSize: '20px', color: 'white' }}></i>
            </button>

            {/* Main Menu */}
            <div style={menuStyle}>
                <button
                    onClick={() => setIscollapsed(true)}
                    style={closeButtonStyle}
                >
                    ✕
                </button>

                <h1 style={{ margin: '0 30px 30px', fontSize: '28px', textAlign: 'center' }}>
                    Share Your Music Graph
                </h1>

                <BuyMeACoffee />

                <div style={imagesContainerStyle}>
                    {/* Normal View Image */}
                    <div
                        style={imageCardStyle}
                        onClick={() => handleImageClick('normal')}
                        onMouseOver={(e) => {
                            e.currentTarget.style.transform = 'translateY(-2px)';
                            e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.5)';
                        }}
                        onMouseOut={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.3)';
                        }}
                    >
                        {previewImages.normal ? (
                            <img src={previewImages.normal} alt="Normal view" style={imageStyle} />
                        ) : (
                            <div style={{
                                ...imageStyle,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: 'rgba(255,255,255,0.1)',
                                fontSize: '16px'
                            }}>
                                <i className="bi bi-hourglass-split" style={{ marginRight: '8px' }}></i>
                                Loading...
                            </div>
                        )}
                        <div style={labelStyle}>
                            <i className="bi bi-diagram-3" style={{ marginRight: '8px' }}></i>
                            Detailed View
                            <div style={{ fontSize: '12px', opacity: 0.8, marginTop: '4px', fontWeight: 'normal' }}>
                                Shows all musician names
                            </div>
                        </div>
                    </div>

                    {/* Zoomed Out View Image */}
                    <div
                        style={imageCardStyle}
                        onClick={() => handleImageClick('zoomed')}
                        onMouseOver={(e) => {
                            e.currentTarget.style.transform = 'translateY(-2px)';
                            e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.5)';
                        }}
                        onMouseOut={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.3)';
                        }}
                    >
                        {previewImages.zoomed ? (
                            <img src={previewImages.zoomed} alt="Zoomed view" style={imageStyle} />
                        ) : (
                            <div style={{
                                ...imageStyle,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: 'rgba(255,255,255,0.1)',
                                fontSize: '16px'
                            }}>
                                <i className="bi bi-hourglass-split" style={{ marginRight: '8px' }}></i>
                                Loading...
                            </div>
                        )}
                        <div style={labelStyle}>
                            <i className="bi bi-tags" style={{ marginRight: '8px' }}></i>
                            Overview
                            <div style={{ fontSize: '12px', opacity: 0.8, marginTop: '4px', fontWeight: 'normal' }}>
                                Shows cluster tags
                            </div>
                        </div>
                    </div>
                </div>

                <p style={{
                    textAlign: 'center',
                    opacity: 0.8,
                    fontSize: '16px',
                    margin: '0',
                    fontStyle: 'italic'
                }}>
                    <i className="bi bi-cursor-fill" style={{ marginRight: '6px' }}></i>
                    Click on an image to share it
                </p>
            </div>

            {/* Share Options Overlay */}
            {selectedImage && (
                <div style={shareMenuStyle}>
                    <button
                        onClick={() => setSelectedImage(null)}
                        style={closeButtonStyle}
                    >
                        ✕
                    </button>

                    <h2 style={{
                        textAlign: 'center',
                        margin: '0 0 20px 0',
                        fontSize: '24px'
                    }}>
                        Share {selectedImage === 'normal' ? 'Detailed' : 'Overview'} Image
                    </h2>


                    <div style={shareButtonsContainerStyle}>
                        {isMobile() ? (
                            // Mobile - Simplified sharing
                            <>
                                <button
                                    style={mobileShareButtonStyle}
                                    onClick={() => handleMobileSave(selectedImage)}
                                    onMouseOver={(e) => {
                                        e.currentTarget.style.transform = 'translateY(-2px)';
                                        e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.5)';
                                    }}
                                    onMouseOut={(e) => {
                                        e.currentTarget.style.transform = 'translateY(0)';
                                        e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                                    }}
                                >
                                    <i className="bi bi-image"></i>
                                    {isIOS() ? 'Save to Photos' : 'Save Image'}
                                </button>
                                <button
                                    style={mobileShareButtonStyle}
                                    onClick={() => handleMobileShare(selectedImage)}
                                    onMouseOver={(e) => {
                                        e.currentTarget.style.transform = 'translateY(-2px)';
                                        e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.5)';
                                    }}
                                    onMouseOut={(e) => {
                                        e.currentTarget.style.transform = 'translateY(0)';
                                        e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                                    }}
                                >
                                    <i className="bi bi-share"></i>
                                    Share Image
                                </button>
                            </>
                        ) : (
                            // Desktop - Full sharing options
                            <>
                                <button
                                    style={shareButtonStyle}
                                    onClick={() => handleDesktopShare('download', selectedImage)}
                                    onMouseOver={(e) => {
                                        e.currentTarget.style.transform = 'translateY(-2px)';
                                        e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.5)';
                                    }}
                                    onMouseOut={(e) => {
                                        e.currentTarget.style.transform = 'translateY(0)';
                                        e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                                    }}
                                >
                                    <i className="bi bi-download"></i>
                                    Download
                                </button>
                                <button
                                    style={shareButtonStyle}
                                    onClick={() => handleDesktopShare('twitter', selectedImage)}
                                    onMouseOver={(e) => {
                                        e.currentTarget.style.transform = 'translateY(-2px)';
                                        e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.5)';
                                    }}
                                    onMouseOut={(e) => {
                                        e.currentTarget.style.transform = 'translateY(0)';
                                        e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                                    }}
                                >
                                    <i className="bi bi-twitter"></i>
                                    Twitter
                                </button>
                                <button
                                    style={shareButtonStyle}
                                    onClick={() => handleDesktopShare('facebook', selectedImage)}
                                    onMouseOver={(e) => {
                                        e.currentTarget.style.transform = 'translateY(-2px)';
                                        e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.5)';
                                    }}
                                    onMouseOut={(e) => {
                                        e.currentTarget.style.transform = 'translateY(0)';
                                        e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                                    }}
                                >
                                    <i className="bi bi-facebook"></i>
                                    Facebook
                                </button>
                                <button
                                    style={shareButtonStyle}
                                    onClick={() => handleDesktopShare('reddit', selectedImage)}
                                    onMouseOver={(e) => {
                                        e.currentTarget.style.transform = 'translateY(-2px)';
                                        e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.5)';
                                    }}
                                    onMouseOut={(e) => {
                                        e.currentTarget.style.transform = 'translateY(0)';
                                        e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                                    }}
                                >
                                    <i className="bi bi-reddit"></i>
                                    Reddit
                                </button>
                                <button
                                    style={shareButtonStyle}
                                    onClick={() => handleDesktopShare('whatsapp', selectedImage)}
                                    onMouseOver={(e) => {
                                        e.currentTarget.style.transform = 'translateY(-2px)';
                                        e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.5)';
                                    }}
                                    onMouseOut={(e) => {
                                        e.currentTarget.style.transform = 'translateY(0)';
                                        e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                                    }}
                                >
                                    <i className="bi bi-whatsapp"></i>
                                    WhatsApp
                                </button>
                                <button
                                    style={shareButtonStyle}
                                    onClick={() => handleDesktopShare('telegram', selectedImage)}
                                    onMouseOver={(e) => {
                                        e.currentTarget.style.transform = 'translateY(-2px)';
                                        e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.5)';
                                    }}
                                    onMouseOut={(e) => {
                                        e.currentTarget.style.transform = 'translateY(0)';
                                        e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                                    }}
                                >
                                    <i className="bi bi-telegram"></i>
                                    Telegram
                                </button>
                            </>
                        )}
                    </div>
                </div>
            )}
        </>
    );
};

export default ShareImagesMenu;